import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Heart, BookOpen, MessageCircle, TrendingUp } from "lucide-react";
import LanguageSelector from "../components/LanguageSelector";

const LandingPage = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('es');

  const t = (es, ca) => language === 'ca' ? ca : es;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      {/* Language Selector */}
      <div className="absolute top-6 right-6 z-50">
        <LanguageSelector currentLanguage={language} onLanguageChange={setLanguage} />
      </div>

      {/* Hero Section */}
      <div className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-teal-400/10 rounded-full blur-3xl -top-20 -left-20 animate-pulse"></div>
          <div className="absolute w-96 h-96 bg-slate-400/10 rounded-full blur-3xl -bottom-20 -right-20 animate-pulse" style={{animationDelay: '1s'}}></div>
          
          {/* Cosmic Origin Circle - Subtle presence, visible for those who seek it */}
          <div className="absolute top-1/3 sm:top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-30">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="w-[350px] h-[350px] sm:w-[450px] sm:h-[450px]">
              <defs>
                <linearGradient id="landingCosmicGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#7FFFD4', stopOpacity: 0.4 }} />
                  <stop offset="33%" style={{ stopColor: '#8A2BE2', stopOpacity: 0.4 }} />
                  <stop offset="66%" style={{ stopColor: '#FFD700', stopOpacity: 0.4 }} />
                  <stop offset="100%" style={{ stopColor: '#7FFFD4', stopOpacity: 0.4 }} />
                </linearGradient>
                <radialGradient id="landingGlowGradient" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" style={{ stopColor: '#7FFFD4', stopOpacity: 0.05 }} />
                  <stop offset="100%" style={{ stopColor: '#7FFFD4', stopOpacity: 0 }} />
                </radialGradient>
                <filter id="landingEtherealBlur">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="0.4" />
                </filter>
              </defs>
              
              {/* Subtle cosmic dust stars */}
              <circle cx="20" cy="15" r="0.5" fill="#FFD700" opacity="0.2" />
              <circle cx="75" cy="22" r="0.6" fill="#FFFFFF" opacity="0.15" />
              <circle cx="30" cy="70" r="0.4" fill="#7FFFD4" opacity="0.2" />
              <circle cx="85" cy="60" r="0.5" fill="#FFD700" opacity="0.15" />
              <circle cx="15" cy="50" r="0.5" fill="#8A2BE2" opacity="0.1" />
              
              {/* Inner glow */}
              <circle cx="50" cy="50" r="40" fill="url(#landingGlowGradient)" />
              
              {/* Ethereal reflection circles - more visible */}
              <g filter="url(#landingEtherealBlur)">
                <circle 
                  cx="50" 
                  cy="50" 
                  r="38" 
                  fill="none" 
                  stroke="url(#landingCosmicGradient)" 
                  strokeWidth="1"
                  opacity="0.5"
                />
                <circle 
                  cx="50" 
                  cy="50" 
                  r="40" 
                  fill="none" 
                  stroke="url(#landingCosmicGradient)" 
                  strokeWidth="0.8"
                  opacity="0.35"
                />
                <circle 
                  cx="50" 
                  cy="50" 
                  r="36" 
                  fill="none" 
                  stroke="url(#landingCosmicGradient)" 
                  strokeWidth="0.7"
                  opacity="0.25"
                />
              </g>
            </svg>
          </div>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto fade-in">
          <div className="mb-4 inline-flex flex-col items-center gap-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-teal-400/20">
              <Sparkles className="w-4 h-4 text-teal-300" />
              <span className="text-sm text-teal-300">{t("Guía de Automaestría Emocional", "Guia d'Autoconeixement Emocional")}</span>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 glow-text">
            {t("Tu Viaje hacia la", "El Teu Viatge cap a")}
            <span className="block mt-2 bg-gradient-to-r from-teal-300 via-purple-200/70 via-teal-200 to-slate-300 bg-clip-text text-transparent">
              {language === "ca" ? (
                <>
                  <span style={{whiteSpace: 'nowrap'}}>l&apos;Autoconeixement</span> Conscient
                </>
              ) : (
                "Automaestría Consciente"
              )}
            </span>
          </h1>

          <p className="text-base sm:text-lg text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            {t(
              "Descubre el poder de la IA consciente para guiarte en tu camino de transformación personal.",
              "Descobreix el poder de la IA conscient per guiar-te en el teu camí de transformació personal."
            )}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              data-testid="comenzar-btn"
              onClick={() => navigate("/auth")}
              className="btn-primary text-lg"
            >
              {t("Comenzar Ahora", "Començar Ara")}
            </button>
            <button
              data-testid="info-btn"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-secondary text-lg"
            >
              {t("Descubre Más", "Descobreix Més")}
            </button>
          </div>

          {/* Pricing */}
          <div className="inline-block glass border border-teal-400/20 rounded-2xl px-6 py-3">
            <p className="text-teal-300 font-semibold mb-1">
              {t("7 días de prueba gratuita", "7 dies de prova gratuïta")}
            </p>
            <p className="text-gray-400 text-sm">
              {t("Después 9,99€/mes o 99€/año", "Després 9,99€/mes o 99€/any")}
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-black/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16 glow-text">
            {t("¿Qué Encontrarás?", "Què Trobaràs?")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div data-testid="feature-card-lessons" className="card glass rounded-2xl p-8 hover:border-teal-400/30">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center mb-6">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-teal-200">{t("Lecciones Profundas", "Lliçons Profundes")}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t(
                  "Contenido cuidadosamente diseñado para guiarte en tu desarrollo personal y espiritual.",
                  "Contingut acuradament dissenyat per guiar-te en el teu desenvolupament personal i espiritual."
                )}
              </p>
            </div>

            <div data-testid="feature-card-ai" className="card glass rounded-2xl p-8 hover:border-teal-400/30">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center mb-6">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-slate-300">{t("Guía con IA", "Guia amb IA")}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {t(
                  "Chat personalizado con inteligencia artificial entrenada en principios de automaestría.",
                  "Xat personalitzat amb intel·ligència artificial entrenada en principis d'autoconeixement."
                )}
              </p>
            </div>

            <div data-testid="feature-card-journal" className="card glass rounded-2xl p-8 hover:border-teal-400/30">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 gold-accent">{t("Diario Personal", "Diari Personal")}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t(
                  "Reflexiona diariamente y recibe insights de IA sobre tu viaje interior.",
                  "Reflexiona diàriament i rep insights d'IA sobre el teu viatge interior."
                )}
              </p>
            </div>

            <div data-testid="feature-card-exercises" className="card glass rounded-2xl p-8 hover:border-teal-400/30">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-purple-300">{t("Ejercicios Prácticos", "Exercicis Pràctics")}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t(
                  "Prácticas diarias para integrar los aprendizajes en tu vida cotidiana.",
                  "Pràctiques diàries per integrar els aprenentatges a la teva vida quotidiana."
                )}
              </p>
            </div>

            <div data-testid="feature-card-progress" className="card glass rounded-2xl p-8 hover:border-teal-400/30">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-green-300">{t("Seguimiento", "Seguiment")}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t(
                  "Visualiza tu progreso y mantén la motivación en tu camino de crecimiento.",
                  "Visualitza el teu progrés i manté la motivació en el teu camí de creixement."
                )}
              </p>
            </div>

            <div data-testid="feature-card-bilingual" className="card glass rounded-2xl p-8 hover:border-teal-400/30">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-rose-300">{t("Bilingüe", "Bilingüe")}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t(
                  "Contenido completo en español y catalán, elige tu idioma preferido.",
                  "Contingut complet en espanyol i català, tria el teu idioma preferit."
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            <span className="bg-gradient-to-r from-teal-300 to-slate-300 bg-clip-text text-transparent">
              {t("Comienza tu Transformación Hoy", "Comença la Teva Transformació Avui")}
            </span>
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            {t(
              "Únete a una comunidad de personas comprometidas con su crecimiento personal.",
              "Uneix-te a una comunitat de persones compromeses amb el seu creixement personal."
            )}
          </p>
          <button
            data-testid="comenzar-footer-btn"
            onClick={() => navigate("/auth")}
            className="btn-primary text-lg px-10 py-4"
          >
            {t("Empezar Gratis", "Començar Gratis")}
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8 px-4">
        <div className="max-w-6xl mx-auto text-center text-gray-500 text-sm">
          <p>© 2025 ESSENC·IA. {t("Todos los derechos reservados.", "Tots els drets reservats.")}</p>
          <div className="mt-2 flex items-center justify-center gap-2">
            <Heart className="w-4 h-4 text-teal-400" />
            <span>{t("Creado con consciencia y amor", "Creat amb consciència i amor")}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;